import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import expenseService from '../../services/ExpenseService.js';
import { Receipt, Pencil, ArrowLeft, FileText } from 'lucide-react';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

const ViewExpense = () => {
  const { expenseId } = useParams();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExpense();
  }, [expenseId]);

  const fetchExpense = async () => {
    try {
      const response = await expenseService.getById(expenseId);
      setExpense(response.data);
    } catch (err) {
      Alert.error('Failed to load expense details');
      console.error(err);
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const getFileAttachmentUrl = () => {
    return expenseService.getFileAttachment(expenseId);
  };

  const openFileModal = () => {
    setIsDocModalOpen(true);
  };

  const closeFileModal = () => {
    setIsDocModalOpen(false);
  };

  const handleApprove = async () => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to approve this expense?',
      'Approve Expense'
    );
    
    if (confirmed) {
      try {
        await expenseService.approve(expenseId);
        Alert.success('Expense approved successfully!');
        fetchExpense();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve expense');
      }
    }
  };

  const handleMarkAsPaid = async () => {
    navigate(`/expenses/edit/${expenseId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <PageLayout title="Expense Details" icon={Receipt}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading expense details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!expense) {
    return null;
  }

  return (
    <>
      <PageLayout
        title="Expense Details"
        icon={Receipt}
        actions={
          <>
            {expense.status === 'pending' && (
              <Button variant="success" onClick={handleApprove}>
                ✓ Approve Expense
              </Button>
            )}
            {(expense.status === 'approved' || expense.status === 'pending') && (
              <Button variant="primary" onClick={handleMarkAsPaid}>
                💰 Mark as Paid
              </Button>
            )}
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => navigate(`/expenses/edit/${expenseId}`)}
            >
              Edit Expense
            </Button>
            <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/expenses')}>
              Back to Expenses
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-primary rounded-card p-4 flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold text-secondary">{expense.expense_id}</h2>
            <StatusBadge status={expense.status} label={expense.status_display} />
            <span className="text-lg font-bold text-gray-900">
              {formatAmount(expense.amount)}
            </span>
          </div>

          <DetailCard title="Basic Information">
            <DetailRow label="Expense ID" value={expense.expense_id} />
            <DetailRow label="Category" value={expense.category_display} />
            <DetailRow label="Month/Year" value={expense.month_year} />
            <DetailRow label="Expense Date" value={formatDate(expense.expense_date)} />
            <DetailRow label="Amount" value={formatAmount(expense.amount)} />
            <DetailRow label="Description" value={expense.description} />
          </DetailCard>

          <DetailCard title="Vendor Information">
            <DetailRow label="Vendor Name" value={expense.vendor_name || 'N/A'} />
            <DetailRow label="Bill Number" value={expense.bill_number || 'N/A'} />
          </DetailCard>

          {expense.status === 'paid' && (
            <DetailCard title="Payment Information">
              <DetailRow
                label="Payment Mode"
                value={
                  expense.payment_mode
                    ? expense.payment_mode.replace(/_/g, ' ').toUpperCase()
                    : 'N/A'
                }
              />
              <DetailRow label="Payment Date" value={formatDateTime(expense.payment_date)} />
              <DetailRow label="Transaction ID" value={expense.transaction_id || 'N/A'} />
              <DetailRow label="Paid To" value={expense.paid_to || 'N/A'} />
              <DetailRow label="Paid By" value={expense.paid_by || 'N/A'} />
            </DetailCard>
          )}

          {expense.status === 'approved' && (
            <DetailCard title="Approval Information">
              <DetailRow label="Approved By" value={expense.approved_by || 'N/A'} />
              <DetailRow label="Approved At" value={formatDateTime(expense.approved_at)} />
            </DetailCard>
          )}

          <DetailCard title="Additional Information">
            <DetailRow
              label="File Attachment"
              value={
                expense.has_attachment ? (
                  <Button variant="primary" size="sm" icon={FileText} onClick={openFileModal}>
                    View Attachment
                  </Button>
                ) : (
                  'No attachment available'
                )
              }
            />
            <DetailRow label="Remarks" value={expense.remarks || 'No remarks'} />
          </DetailCard>

          <DetailCard title="System Information">
            <DetailRow label="Created By" value={expense.created_by} />
            <DetailRow label="Created At" value={formatDateTime(expense.created_at)} />
            <DetailRow label="Last Updated" value={formatDateTime(expense.updated_at)} />
            <DetailRow
              label="Status"
              value={<StatusBadge status={expense.status} label={expense.status_display} />}
            />
          </DetailCard>
        </div>
      </PageLayout>

      <DocumentModal
        isOpen={isDocModalOpen}
        onClose={closeFileModal}
        documentUrl={getFileAttachmentUrl()}
        documentType="expense_attachment"
      />
    </>
  );
};

export default ViewExpense;
